/**
 * Datagrid component displays the datagrid with all informations for variation analysis.
 * This component handles the first user inputs for different actions.
 */

import React, { useMemo, useCallback } from "react";
import DataGrid, {
  Column,
  Grouping,
  Export,
  GroupPanel,
  FilterRow,
  SearchPanel,
  Scrolling,
  Editing,
  LoadPanel,
} from "devextreme-react/data-grid";
import saveAs from "file-saver";
import ProgressBarCell from "components/common/pyramid/ProgressBarCell";
import { osirisApi } from "services/api/osiris";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import { formatUTCtoLocal } from "components/common/pyramid/FormatUTCtoLocal";

/**
 * Props:
 * - companyList: list of companies
 * - userList: list of users
 * - variationList: list of variation analyses
 * - datasetList: list of datasets
 * - onAddButtonClick: function to handle the add button click
 * - onRowClick: function to handle the row click
 * - isLoading: boolean indicating if data is loading
 */
const Datagrid = ({
  companyList,
  userList,
  variationList,
  datasetList,
  onAddButtonClick,
  onRowClick,
  isLoading,
}) => {
  const osiris = osirisApi();

  const allowDeletion = useCallback(
    (e) => {
      return e.row.data.groupId === companyList[0]?.id;
    },
    [companyList]
  );

  const onExporting = useCallback((e) => {
    e.component.beginUpdate();
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Variation Analysis");

    exportDataGrid({
      component: e.component,
      worksheet: worksheet,
      autoFilterEnabled: true,
    })
      .then(function () {
        workbook.xlsx.writeBuffer().then(function (buffer) {
          saveAs(
            new Blob([buffer], { type: "application/octet-stream" }),
            "VariationAnalysis.xlsx"
          );
        });
      })
      .then(function () {
        e.component.endUpdate();
      });
  }, []);

  // Saving the toolbar items by memoization
  const toolbarItems = useMemo(
    () => [
      {
        widget: "dxButton",
        options: {
          icon: "refresh",
          onClick: () => {
            window.dispatchEvent(new CustomEvent("refreshVariationAnalyses"));
          },
        },
        location: "after",
      },
      {
        widget: "dxButton",
        options: {
          icon: "add",
          onClick: onAddButtonClick,
        },
        location: "after",
      },
    ],
    [onAddButtonClick]
  );

  const onToolbarPreparing = (e) => {
    e.toolbarOptions.items.unshift(...toolbarItems);
  };

  const deleteVariationAnalysis = useCallback(
    async (e) => {
      try {
        await osiris.deleteVariationAnalysis(e.key);
        window.dispatchEvent(new CustomEvent("refreshVariationAnalyses"));
      } catch (error) {
        console.error("Error deleting variation analysis:", error);
      }
    },
    [osiris]
  );

  // Datagrid columns
  const columns = useMemo(
    () => [
      {
        dataField: "groupId",
        caption: "Created by company",
        groupIndex: 0,
        lookup: {
          dataSource: companyList,
          valueExpr: "id",
          displayExpr: "displayName",
        },
        groupCellTemplate: (element, data) => element.append(data.text),
      },
      {
        dataField: "userId",
        caption: "Created by user",
        groupIndex: 1,
        lookup: {
          dataSource: userList,
          valueExpr: "id",
          displayExpr: "displayName",
        },
        groupCellTemplate: (element, data) => element.append(data.text),
      },
      {
        dataField: "description",
        caption: "Description",
      },
      {
        dataField: "createdAt",
        caption: "Creation Date",
        dataType: "date",
        cellRender: (cellData) => {
          return formatUTCtoLocal(cellData.value);
        },
        calculateCellValue: (data) => {
          if (!data.createdAt) return null;
          const localDate = formatUTCtoLocal(data.createdAt);
          return typeof localDate === "string"
            ? new Date(localDate)
            : localDate;
        },
      },
      {
        dataField: "datasetDepartureId",
        caption: "Input departure",
        lookup: {
          dataSource: datasetList,
          valueExpr: "id",
          displayExpr: "description",
        },
      },
      {
        dataField: "datasetArrivalId",
        caption: "Input arrival",
        lookup: {
          dataSource: datasetList,
          valueExpr: "id",
          displayExpr: "description",
        },
      },
      {
        dataField: "step",
        caption: "Step",
        alignment: "center",
        cellRender: (cellData) => (
          <ProgressBarCell
            value={cellData.value}
            errorMessage={cellData.data.errorMessage}
          />
        ),
      },
    ],
    [companyList, userList, datasetList, deleteVariationAnalysis]
  );

  // Datagrid options
  const gridOptions = useMemo(
    () => ({
      dataSource: variationList,
      keyExpr: "id",
      rowAlternationEnabled: false,
      showBorders: false,
      showColumnLines: false,
      hoverStateEnabled: true,
      allowColumnResizing: true,
      width: "100%",
      height: "60vh",
    }),
    [variationList]
  );

  const onCellPrepared = useCallback((e) => {
    if (e.rowType === "data") {
      e.cellElement.style.setProperty("cursor", "pointer", "important");
    }
  }, []);

  const onRowClicked = useCallback(
    (e) => {
      if (e.rowType === "data") {
        onRowClick(e.data);
      }
    },
    [onRowClick]
  );

  return (
    <div style={{ width: "100%" }}>
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      <DataGrid
        {...gridOptions}
        onToolbarPreparing={onToolbarPreparing}
        onExporting={onExporting}
        onRowRemoving={deleteVariationAnalysis}
        onCellPrepared={onCellPrepared}
        onRowClick={onRowClicked}
      >
        {/* Additional parameters */}
        <FilterRow visible={true} applyFilter="auto" />
        <Scrolling mode="virtual" />
        <GroupPanel visible={true} allowColumnDragging={false} />
        <SearchPanel visible={true} highlightCaseSensitive={true} />
        <Grouping autoExpandAll={true} />
        <Export enabled={true} />
        <Editing
          mode="row"
          allowDeleting={allowDeletion}
          texts={{
            deleteRow: "",
          }}
          useIcons={true}
        />
        <LoadPanel
          indicatorSrc="/assets/img/bootstrap-spinner.svg"
          enabled={false}
        />

        {columns.map((column) => (
          <Column
            key={`${column.dataField}-${column.name || ""}`}
            {...column}
          />
        ))}
      </DataGrid>
    </div>
  );
};

export default React.memo(Datagrid);
